import { Route, match } from "react-router-dom";
import BlueprintEditorLeftPanel from "./left-panel/BlueprintEditorLeftPanel";
import BlueprintEditorRightPanel from "./right-panel/BlueprintEditorRightPanel";
import BlueprintCanvas from "./canvas/BlueprintCanvas";
import { JSONObjectSchema, SchemaTypes } from "../../models/Blueprints";
import BlueprintEditorTopControlPanel from "./control-panel/BlueprintEditorTopControlPanel";
import {
  BLUEPRINT_EDITOR_MAIN_TAB_PATH,
  BLUEPRINT_EDITOR_README_TAB_PATH,
  BLUEPRINT_EDITOR_VALIDATION_TAB_PATH,
  BLUEPRINT_EDITOR_RETURN_SCHEMA_TAB_PATH,
} from "../../router/RouterUtils";
import BlueprintValidationEditor from "./validator/BlueprintValidationEditor";
import BlueprintEditorReadmePanel from "./center-panel/BlueprintEditorReadmePanel";
import useBlueprintContext from "./context/useBlueprintContext";
import { BlueprintChatWidget } from "./chat-bpe/components/BlueprintChatWidget";
import { useEffect, useRef, useState } from "react";

const PANEL_DEFAULT_LEFT_WIDTH = 350;
const PANEL_DEFAULT_RIGHT_WIDTH = 350;
const PANEL_MIN_WIDTH = 280;
const PANEL_MAX_WIDTH = 700;
// Width of the two ResizeDividers combined, used when clamping on window resize.
const RESIZE_DIVIDERS_WIDTH = 16;

type Props = {
  integrationID: string;
  match: match;
  parameterSchema: undefined | JSONObjectSchema;
  returnSchema: undefined | JSONObjectSchema;
  operationType: undefined | string;
  hasUnsavedChanges: boolean;
};

const RESIZE_DIVIDER_CLASS =
  "w-2 cursor-col-resize shrink-0 relative z-10 " +
  "after:content-[''] after:absolute after:top-0 after:left-1/2 after:-translate-x-1/2 " +
  "after:w-2 after:h-full after:bg-transparent after:transition-colors after:duration-150 " +
  "hover:after:bg-[#75b0e0] active:after:bg-[#75b0e0]";

const BlueprintEditorView = ({
  parameterSchema,
  returnSchema,
  operationType,
  hasUnsavedChanges,
}: Props) => {
  // context
  const { blueprint, doesBlueprintHaveUnsavedChanges } = useBlueprintContext();

  // state
  const [isCustomerLinkedAccountSelected, setIsCustomerLinkedAccountSelected] = useState<boolean>(
    false
  );
  const [leftPanelWidth, setLeftPanelWidth] = useState(PANEL_DEFAULT_LEFT_WIDTH);
  const [rightPanelWidth, setRightPanelWidth] = useState(PANEL_DEFAULT_RIGHT_WIDTH);
  const containerRef = useRef<HTMLDivElement>(null);

  // On window resize, keep panel pixel widths fixed but clamp them down if the
  // window has shrunk enough that a panel would violate the minimum width.
  useEffect(() => {
    const onResize = () => {
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? window.innerWidth;
      const maxAllowed = Math.max(
        PANEL_MIN_WIDTH,
        containerWidth - PANEL_MIN_WIDTH - RESIZE_DIVIDERS_WIDTH
      );
      setLeftPanelWidth((w) => Math.min(w, maxAllowed));
      setRightPanelWidth((w) => Math.min(w, maxAllowed));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLeftResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftPanelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMouseMove = (ev: MouseEvent) => {
      setLeftPanelWidth(
        Math.min(Math.max(PANEL_MIN_WIDTH, startWidth + (ev.clientX - startX)), PANEL_MAX_WIDTH)
      );
    };
    const onMouseUp = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleRightResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = rightPanelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMouseMove = (ev: MouseEvent) => {
      // dragging left increases the right panel width
      setRightPanelWidth(
        Math.min(Math.max(PANEL_MIN_WIDTH, startWidth - (ev.clientX - startX)), PANEL_MAX_WIDTH)
      );
    };
    const onMouseUp = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div>
      <BlueprintEditorTopControlPanel hasUnsavedChanges={hasUnsavedChanges} />
      <div ref={containerRef} className="h-[calc(100vh-64px)] grow flex">
        <BlueprintEditorLeftPanel
          width={leftPanelWidth}
          isCustomerLinkedAccountSelected={isCustomerLinkedAccountSelected}
          setIsCustomerLinkedAccountSelected={setIsCustomerLinkedAccountSelected}
        />
        <div className={RESIZE_DIVIDER_CLASS} onMouseDown={handleLeftResizeMouseDown} />
        <Route path={BLUEPRINT_EDITOR_VALIDATION_TAB_PATH}>
          {parameterSchema && (
            <BlueprintValidationEditor
              schema={parameterSchema}
              schemaType={SchemaTypes.parameter_schema}
              operationType={operationType}
            />
          )}
        </Route>
        <Route path={BLUEPRINT_EDITOR_RETURN_SCHEMA_TAB_PATH}>
          {returnSchema && (
            <BlueprintValidationEditor
              schema={returnSchema}
              schemaType={SchemaTypes.return_schema}
              operationType={operationType}
            />
          )}
        </Route>
        <Route path={BLUEPRINT_EDITOR_MAIN_TAB_PATH}>
          <BlueprintCanvas />
          <div className={RESIZE_DIVIDER_CLASS} onMouseDown={handleRightResizeMouseDown} />
          <BlueprintEditorRightPanel width={rightPanelWidth} />
        </Route>
        <Route path={BLUEPRINT_EDITOR_README_TAB_PATH}>
          <BlueprintEditorReadmePanel />
        </Route>
      </div>
      {!isCustomerLinkedAccountSelected && (
        <BlueprintChatWidget
          doesBlueprintHaveUnsavedChanges={doesBlueprintHaveUnsavedChanges}
          blueprint={blueprint}
          isUsingCustomerLinkedAccount={isCustomerLinkedAccountSelected}
        />
      )}
    </div>
  );
};

export default BlueprintEditorView;
